import { motion } from "framer-motion";
import { Sparkles, CheckCircle2 } from "lucide-react";
import { STATS } from "@/lib/mockData";
import Counter from "@/components/Counter";

const EASE = [0.22, 1, 0.36, 1];

const BULLETS = [
  "AI-powered talent sourcing and strategic recruitment solutions",
  "Global hiring expertise across the United States, Europe & Canada",
  "Dedicated recruitment specialists and scalable hiring support",
  "Executive Search, Permanent Placement, Contract Staffing & RPO Services",
  "Industry-focused recruitment across multiple specialized sectors",
  "Faster hiring, higher-quality candidates, and long-term workforce solutions",
];

const About = () => {
  return (
    <section id="about" className="relative py-20 md:py-28 bg-white">
      <div className="px-5 md:px-10 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        {/* Left: image collage */}
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: EASE }}
          className="lg:col-span-5 relative"
        >
          <div className="relative">
            <div className="absolute -top-4 -left-4 w-24 h-24 rounded-2xl bg-brand-blue hidden md:block" />
            <div className="absolute -bottom-4 -right-4 w-24 h-24 rounded-2xl border-2 border-brand-red hidden md:block" />
            <div className="relative overflow-hidden rounded-2xl aspect-[4/5] bg-brand-surface">
              <img
                src="https://images.pexels.com/photos/3184291/pexels-photo-3184291.jpeg?auto=compress&cs=tinysrgb&w=1200"
                alt="Professional team reviewing hiring strategy"
                className="absolute inset-0 w-full h-full object-cover"
                loading="lazy"
              />
            </div>

            {/* Floating badge */}
            <div className="absolute -right-4 md:-right-8 top-8 md:top-12 bg-white card-soft rounded-xl px-4 md:px-5 py-3 md:py-4 flex items-center gap-3 max-w-[220px]">
              <div className="w-10 h-10 rounded-lg bg-brand-red-light flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-5 h-5 text-brand-red" strokeWidth={2} />
              </div>
              <div>
                <div className="font-display font-bold text-brand-ink leading-tight text-sm md:text-base">
                  AI-Powered
                </div>
                <div className="text-xs text-brand-muted leading-tight">Recruitment</div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Right: copy */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: EASE, delay: 0.1 }}
          className="lg:col-span-7"
        >
          <div className="text-xs md:text-sm font-display font-semibold tracking-[0.2em] uppercase text-brand-red mb-3">
            About Us
          </div>
          <h2 className="section-heading font-display font-bold text-4xl md:text-5xl text-brand-ink leading-tight">
            Fifteen years of<br />
            <span className="text-brand-blue">precise placements.</span>
          </h2>

          <p className="mt-8 text-brand-slate text-base md:text-[17px] leading-relaxed max-w-2xl">
            SNV Elite Solutions is a trusted global recruitment and staffing
            partner that combines advanced AI-powered sourcing with over 15
            years of recruitment expertise to help businesses hire exceptional
            talent faster. We support organizations across the United States,
            Europe, and Canada with end-to-end Recruitment Process Outsourcing
            (RPO), executive search, permanent placement, contract staffing,
            and customized workforce solutions. Whether you&apos;re scaling a
            startup, growing a mid-sized business, or expanding an enterprise
            workforce, we deliver qualified professionals across Construction,
            Healthcare, Engineering, Manufacturing, Information Technology,
            Finance, Accounting, Logistics, Supply Chain, Executive Leadership,
            and other specialized industries—helping you reduce hiring time,
            improve candidate quality, and build high-performing teams with
            confidence.
          </p>

          <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
            {BULLETS.map((b) => (
              <li key={b} className="flex items-start gap-3 text-brand-slate">
                <CheckCircle2 className="w-5 h-5 text-brand-blue mt-0.5 flex-shrink-0" strokeWidth={2} />
                <span className="text-sm md:text-base">{b}</span>
              </li>
            ))}
          </ul>

          {/* Stats */}
          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 border-t border-brand-border pt-8">
            {STATS.map((s, i) => (
              <motion.div
                key={s.label}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5, ease: EASE, delay: 0.05 * i }}
              >
                <div className="font-display font-bold text-3xl md:text-4xl text-brand-blue tabular-nums">
                  <Counter value={s.value} />
                </div>
                <div className="text-xs md:text-sm text-brand-muted mt-1">
                  {s.label}
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default About;
